/**
 * The default styling rule: how each catalog frame shape relates to each confirmed face shape.
 *
 * A styling convention, not a physical truth. Frames are advised by contrast and balance — angles for soft features, curves for strong angles, depth for a long face, weight where the face is narrow — and optical guides and individual faces vary a great deal, so v1 is deliberately conservative. C means complementary under this rule. N means the rule makes no claim either way, which is the answer wherever guides differ. L means less preferred by this default rule, reserved for the few cases where a frame repeats the one trait the rule contrasts; it never means wrong, unsuitable or not to be worn, and no screen shows these letters.
 *
 * Every one of the 63 cells is written out, with its reason. Nothing is derived from the fact that a face shape and a frame shape share a name.
 */
import { FaceShape, FrameShape, type NamedFaceShape } from '@optiflow/types';

export const StyleTier = {
  C: 'C',
  N: 'N',
  L: 'L',
} as const;
export type StyleTier = (typeof StyleTier)[keyof typeof StyleTier];

/** The one idea behind a face shape's complementary frames, which a complement reason cites. */
export const StylePrinciple = {
  BALANCED_PROPORTIONS: 'BALANCED_PROPORTIONS',
  ADDS_ANGLES: 'ADDS_ANGLES',
  SOFTENS_ANGLES: 'SOFTENS_ANGLES',
  ADDS_DEPTH: 'ADDS_DEPTH',
  BALANCES_FOREHEAD: 'BALANCES_FOREHEAD',
  WIDENS_BROW: 'WIDENS_BROW',
  BALANCES_JAW: 'BALANCES_JAW',
} as const;
export type StylePrinciple = (typeof StylePrinciple)[keyof typeof StylePrinciple];

export interface StyleCell {
  readonly tier: StyleTier;
  readonly rationale: string;
}

export type StyleRow = Readonly<Record<FrameShape, StyleCell>>;
export type StyleMatrix = Readonly<Record<NamedFaceShape, StyleRow>>;

const C = (rationale: string): StyleCell => ({ tier: StyleTier.C, rationale });
const N = (rationale: string): StyleCell => ({ tier: StyleTier.N, rationale });
const L = (rationale: string): StyleCell => ({ tier: StyleTier.L, rationale });

const WRAP_RATIONALE = 'A sport and sun form; the default rule makes no face-shape claim';
const OTHER_RATIONALE =
  'Recorded as a shape outside the named list; the default rule has no detail to use';

export const STYLE_MATRIX_V1: StyleMatrix = {
  [FaceShape.OVAL]: {
    [FrameShape.ROUND]: N('Soft curves echo the face: balanced, but they add no contrast'),
    [FrameShape.SQUARE]: C('Defined corners add structure to balanced proportions'),
    [FrameShape.RECTANGLE]: C(
      'An angular horizontal line adds structure without unbalancing the face',
    ),
    [FrameShape.OVAL]: N('Repeats the face outline: balanced, but it adds no contrast'),
    [FrameShape.AVIATOR]: C('A strong top bar and a low curve sit well on balanced proportions'),
    [FrameShape.CAT_EYE]: C('Upswept corners add character without unbalancing the face'),
    [FrameShape.WAYFARER]: C('A defined top line adds definition to balanced proportions'),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
  [FaceShape.ROUND]: {
    [FrameShape.ROUND]: L(
      'Repeats the round outline, the one trait the default rule contrasts with angles',
    ),
    [FrameShape.SQUARE]: C('Corners contrast soft, rounded features'),
    [FrameShape.RECTANGLE]: C(
      'An angular, wider-than-tall line adds definition and apparent length',
    ),
    [FrameShape.OVAL]: N(
      'Curved like the face but wider than tall; guides differ, so no claim either way',
    ),
    [FrameShape.AVIATOR]: N(
      'The top bar adds line, but teardrop curves stay soft; no clear effect',
    ),
    [FrameShape.CAT_EYE]: C('Upswept corners add angles and lift'),
    [FrameShape.WAYFARER]: C('An angular trapezoid with a strong top line adds definition'),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
  [FaceShape.SQUARE]: {
    [FrameShape.ROUND]: C('Curves contrast a strong jaw and brow'),
    [FrameShape.SQUARE]: L(
      "Repeats the face's corners, the one trait the default rule softens with curves",
    ),
    [FrameShape.RECTANGLE]: L(
      'Angular lines repeat a straight jaw, the trait the default rule softens with curves',
    ),
    [FrameShape.OVAL]: C('Soft curves balance angular features'),
    [FrameShape.AVIATOR]: C('The rounded lower curve softens the jaw line'),
    [FrameShape.CAT_EYE]: N(
      'The upswept curve softens, but pointed corners keep some angle; no clear effect',
    ),
    [FrameShape.WAYFARER]: N('Tapered but still angular; no clear effect either way'),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
  [FaceShape.OBLONG]: {
    [FrameShape.ROUND]: N('Adds some width, but depth varies too much by model to claim more'),
    [FrameShape.SQUARE]: C('Deeper lenses break up the length of the face'),
    [FrameShape.RECTANGLE]: N(
      'Often shallow, which some guides avoid on a long face, but depth varies by model; no claim',
    ),
    [FrameShape.OVAL]: N('Moderate depth and soft curves; no clear effect either way'),
    [FrameShape.AVIATOR]: C('Deep lenses under a strong top bar shorten apparent length'),
    [FrameShape.CAT_EYE]: N('Adds width at the brow but little depth; no clear effect'),
    [FrameShape.WAYFARER]: C('A bold, deep front adds depth and width'),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
  [FaceShape.HEART]: {
    [FrameShape.ROUND]: C('Soft curves with lower visual weight balance a wider forehead'),
    [FrameShape.SQUARE]: N('Even weight top and bottom; no clear effect either way'),
    [FrameShape.RECTANGLE]: N('Even horizontal weight; no clear effect either way'),
    [FrameShape.OVAL]: C('Soft curves draw attention away from a wider forehead'),
    [FrameShape.AVIATOR]: C('Weight in the lower lens balances a narrow chin'),
    [FrameShape.CAT_EYE]: N(
      'Upswept corners add weight at the brow, but many fronts are light; guides differ, so no claim',
    ),
    [FrameShape.WAYFARER]: N(
      'A heavier top line adds brow weight, but proportions vary widely by model; no claim',
    ),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
  [FaceShape.DIAMOND]: {
    [FrameShape.ROUND]: N('Softens the cheekbones but adds little at the brow; no clear effect'),
    [FrameShape.SQUARE]: N('Some brow width, but angular at cheekbone level; no clear effect'),
    [FrameShape.RECTANGLE]: N('A horizontal line at cheekbone level; no clear effect either way'),
    [FrameShape.OVAL]: C('Soft curves complement prominent cheekbones'),
    [FrameShape.AVIATOR]: N(
      'The top bar adds brow width, but the deep lower curve sits at the cheekbones; no clear effect',
    ),
    [FrameShape.CAT_EYE]: C('An upswept brow line adds width above the cheekbones'),
    [FrameShape.WAYFARER]: C('A strong top line adds width at a narrow forehead'),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
  [FaceShape.TRIANGLE]: {
    [FrameShape.ROUND]: N('Shape alone says little about top weight; no clear effect'),
    [FrameShape.SQUARE]: N('Even weight top and bottom; no clear effect either way'),
    [FrameShape.RECTANGLE]: N('Even horizontal weight; no clear effect either way'),
    [FrameShape.OVAL]: N('Even weight and soft curves; no clear effect either way'),
    [FrameShape.AVIATOR]: C('A strong top bar adds width at the forehead'),
    [FrameShape.CAT_EYE]: C('Upswept, top-weighted corners balance a wider jaw'),
    [FrameShape.WAYFARER]: C('A heavy top line adds forehead width'),
    [FrameShape.WRAP]: N(WRAP_RATIONALE),
    [FrameShape.OTHER]: N(OTHER_RATIONALE),
  },
};

export const STYLE_PRINCIPLES_V1: Readonly<Record<NamedFaceShape, StylePrinciple>> = {
  [FaceShape.OVAL]: StylePrinciple.BALANCED_PROPORTIONS,
  [FaceShape.ROUND]: StylePrinciple.ADDS_ANGLES,
  [FaceShape.SQUARE]: StylePrinciple.SOFTENS_ANGLES,
  [FaceShape.OBLONG]: StylePrinciple.ADDS_DEPTH,
  [FaceShape.HEART]: StylePrinciple.BALANCES_FOREHEAD,
  [FaceShape.DIAMOND]: StylePrinciple.WIDENS_BROW,
  [FaceShape.TRIANGLE]: StylePrinciple.BALANCES_JAW,
};
